<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import { computed, ref, useAttrs } from 'vue';
import { formatLocalizedNumber, parseLocalizedNumber } from '@/utils/number';
import InputGroupInput from './InputGroupInput.vue';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
    inheritAttrs: false,
});

const props = defineProps<{
    modelValue?: number | null
    locale: string
    formatOptions?: Intl.NumberFormatOptions
    class?: HTMLAttributes['class']
    style?: HTMLAttributes['style']
    required?: boolean
    invalid?: boolean
    disabled?: boolean
}>();

const emits = defineEmits<{
    'update:modelValue': [payload: number | null]
}>();

const attrs = useAttrs();

const isFocused = ref(false);
const typedText = ref('');

const value = computed<number | null>(() => props.modelValue ?? null);
const formattedValue = computed(() => formatLocalizedNumber(value.value, props.locale, props.formatOptions));

// While focused the raw text is left exactly as typed — reformatting mid-entry would move the caret.
const displayText = computed(() => (isFocused.value ? typedText.value : formattedValue.value));

function toBoundary(raw: unknown): number | null {
    const boundary = Number(raw);

    return raw === '' || raw === null || raw === undefined || !Number.isFinite(boundary) ? null : boundary;
}

const min = computed(() => toBoundary(attrs.min));
const max = computed(() => toBoundary(attrs.max));

function clamp(candidate: number): number {
    if (min.value !== null && candidate < min.value) {
        return min.value;
    }

    if (max.value !== null && candidate > max.value) {
        return max.value;
    }

    return candidate;
}

// min/max drive the clamping instead of native constraint validation, `name` goes to the hidden
// field, and `type`/`inputmode` are set here, so none of them belong on the element as well.
const HANDLED_ATTRS = ['min', 'max', 'step', 'name', 'type', 'inputmode'];

const forwardedAttrs = computed(() => Object.fromEntries(
    Object.entries(attrs).filter(([attr]) => !HANDLED_ATTRS.includes(attr)),
));

const name = computed(() => (typeof attrs.name === 'string' && attrs.name !== '' ? attrs.name : null));

// The visible field holds localized text, so a native form submit would post "1,5". The hidden
// field carries the canonical form the backend can parse.
const canonicalValue = computed(() => (value.value === null ? '' : String(value.value)));

function commit(): void {
    if (typedText.value.trim() === '') {
        if (value.value !== null) {
            emits('update:modelValue', null);
        }
        typedText.value = '';

        return;
    }

    const parsed = parseLocalizedNumber(typedText.value);

    if (parsed === null) {
        typedText.value = formattedValue.value;

        return;
    }

    const clamped = clamp(parsed);

    if (clamped !== value.value) {
        emits('update:modelValue', clamped);
    }

    typedText.value = formatLocalizedNumber(clamped, props.locale, props.formatOptions);
}

function onFocus(): void {
    typedText.value = formattedValue.value;
    isFocused.value = true;
}

function onBlur(): void {
    commit();
    isFocused.value = false;
}
</script>

<template>
    <InputGroupInput
        v-bind="forwardedAttrs"
        :model-value="displayText"
        type="text"
        inputmode="decimal"
        :class="props.class"
        :style="props.style"
        :required="props.required"
        :invalid="props.invalid"
        :disabled="props.disabled"
        @update:model-value="typedText = String($event ?? '')"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.enter="commit"
    />
    <input
        v-if="name"
        type="hidden"
        :name="name"
        :value="canonicalValue"
    />
</template>
